import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View
} from "@react-pdf/renderer"
import { jwtDecode } from "jwt-decode"
import moment from "moment"
import { RowExportTypePdf } from "types"

// Registrasi font
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/roboto/v20/KFOmCnqEu92Fr1Mu4mxP.ttf" // Regular
    },
    {
      src: "https://fonts.gstatic.com/s/roboto/v20/KFOlCnqEu92Fr1MmWUlfBBc9.ttf", // Bold
      fontWeight: "bold"
    }
  ]
})

Font.register({
  family: "FontAwesome",
  src: "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/fonts/fontawesome-webfont.ttf"
})

// Types
type TextAlign = "left" | "right" | "center" | "justify"

// Gaya
export const styles = StyleSheet.create({
  page: {
    paddingHorizontal: 15,
    paddingBottom: 35,
    paddingTop: 70
  },
  title: {
    fontSize: 15,
    marginBottom: 3,
    fontFamily: "Roboto"
  },
  date: {
    fontSize: 10,
    marginBottom: 3,
    textAlign: "left",
    fontWeight: "bold",
    fontFamily: "Roboto"
  },
  table: {
    display: "flex",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#fff"
  },
  tableRow: {
    flexDirection: "row"
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#000000",
    color: "#FFFFFF"
  },
  tableCol: {
    flex: 1,
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    borderBottomColor: "#0aeaeb4",
    padding: 2
  },
  tableCell: {
    margin: 2,
    fontSize: 6,
    fontFamily: "Roboto",
    textAlign: "left"
  },
  headerText: {
    margin: 2,
    fontSize: 6,
    fontWeight: "bold",
    fontFamily: "Roboto"
  },
  fixed: {
    bottom: 0,
    left: 0,
    width: '100%',
    padding: 10,
    position: 'absolute',
    flex: 1,
    flexDirection: "row",
    justifyContent: "space-between"
  },
  text: {
    fontSize: 8
  },
  fixedTop: {
    top: 0,
    left: 0,
    width: '100%',
    padding: 10,
    position: 'absolute'
  },
  image: {
    width: 50,
    height: 30,
  }
})

// Komponen utama
export function PdfTemplate({
  data,
  module,
  title,
  column,
  date,
  dateLabel,
  subtitle,
  marginTable
}: {
  data: RowExportTypePdf[]
  module?: string
  title: string
  column: { header: string; key: string | number; width: number; textAlign?: TextAlign }[]
  date?: string
  dateLabel?: string
  subtitle?: string
  marginTable?: number
}) {
  // Vars
  const userData: { company_name?: string } = jwtDecode(localStorage.getItem("token") ?? "")

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View
          fixed
          style={styles.fixedTop}
        >
          <Text style={styles.title}>{module}</Text>
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.date}>{subtitle}</Text>}
          {date && <Text style={styles.date}>{dateLabel ?? "Date:"} {date}</Text>}
        </View>

        {marginTable && <View style={{ marginTop: marginTable ? marginTable : 0 }}></View>}
        <TableCell data={data} column={column} /> 

        <View
          fixed
          style={styles.fixed}
        >
          <Text style={styles.text}>{title} | {userData.company_name ?? "-"} | {moment().format("DD MMM YYYY")}</Text>

          <Text
            style={styles.text}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  )
}

// Komponen tabel
export function TableCell({
  data,
  column
}: {
  data: RowExportTypePdf[]
  column: { header: string; key: string | number; width: number; textAlign?: TextAlign }[]
}) {
  return (
    <View style={styles.table}>
      {/* Header */}
      <View
        fixed
        style={styles.tableHeader}
      >
        {column.map((col, index) => (
          <View key={index} style={[styles.tableCol, { flex: col.width, textAlign: col.textAlign ?? "left" }]}>
            <Text style={styles.headerText}>{col.header}</Text>
          </View>
        ))}
      </View>

      {/* Data */}
      {data.map((row, rowIndex) => (
        <View wrap={false} key={rowIndex} style={styles.tableRow}>
          {column.map((col, colIndex) => {
            const cellData = row[col.key]
            const cellText =
              typeof cellData === "string" || typeof cellData === "number"
                ? cellData
                : cellData?.text ?? ""
            const cellStyle =
              typeof cellData === "object" && cellData?.style
                ? [styles.tableCell, cellData.style]
                : styles.tableCell

            return (
              <View
                key={colIndex}
                style={[styles.tableCol, { flex: col.width }]}>
                <Text style={cellStyle} wrap={true}>
                  {cellText}
                </Text>
              </View>
            )
          })}
        </View>
      ))}
    </View>
  )
}